import React, { useMemo } from 'react';
import { Check, Search } from 'lucide-react';
import { CompactFooter } from './BoardSearchCompactFooter';
import { BoardSearchPickerShell } from './boardSearchPickerShell';

export function BoardSearchListFilterPanel({
  searchPlaceholder,
  showSearch = true,
  searchTerm = '',
  onSearchChange,
  countLabel = 'itens',
  items = [],
  getKey,
  getLabel,
  getSuffix,
  getDescription,
  isItemDisabled,
  isSelected,
  selectedKeys = [],
  onToggle,
  onToggleAll,
  emptyMessage = 'Nenhum resultado',
  listAriaLabel = 'Opções do filtro',
  onClear,
  onApply,
  applyLabel,
  headerBanner = null,
}) {
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const resolveSelected = (item) =>
    (isSelected ? isSelected(item) : selectedSet.has(getKey(item)));

  const visibleCount = items.length;
  const countText = `${visibleCount} ${countLabel}`;

  const allMarked =
    items.length > 0 && items.every((item) => !isItemDisabled?.(item) && resolveSelected(item));

  return (
    <BoardSearchPickerShell
      footer={
        <CompactFooter onClear={onClear} onApply={onApply} applyLabel={applyLabel} />
      }
    >
      {headerBanner ? <div className="bsf-picker-banner">{headerBanner}</div> : null}
      {showSearch ? (
        <div className="bsf-cidade-picker-search">
          <div className="bsf-cidade-picker-search-inner">
            <Search size={14} strokeWidth={2} className="bsf-cidade-picker-search-icon" aria-hidden />
            <input
              type="text"
              className="bsf-cidade-picker-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
            />
            <span className="bsf-cidade-picker-count">{countText}</span>
          </div>
        </div>
      ) : null}

      <div className="bsf-cidade-picker-list" role="listbox" aria-label={listAriaLabel}>
        {onToggleAll && items.length > 1 ? (
          <button
            type="button"
            className={`bsf-cidade-picker-group-head${allMarked ? ' is-selected' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onToggleAll();
            }}
          >
            <span className="bsf-cidade-picker-group-head-label">
              {allMarked ? 'Desmarcar todos' : 'Marcar todos'}
            </span>
            <Check size={14} strokeWidth={2.5} className="bsf-cidade-picker-check" aria-hidden />
          </button>
        ) : null}

        {items.length > 0 ? (
          items.map((item) => {
            const key = getKey(item);
            const selected = resolveSelected(item);
            const disabled = isItemDisabled?.(item);
            const description = getDescription?.(item);
            const suffix = getSuffix?.(item);

            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={disabled}
                className={`bsf-cidade-picker-item${selected ? ' is-selected' : ''}${
                  disabled ? ' is-disabled' : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (!disabled) onToggle(item);
                }}
              >
                <span
                  className={`bsf-cidade-picker-item-label${
                    description ? ' bsf-cidade-picker-item-label--stacked' : ''
                  }`}
                  title={getLabel(item)}
                >
                  {getLabel(item)}
                  {description ? (
                    <span className="bsf-cidade-picker-item-desc">{description}</span>
                  ) : null}
                </span>
                {suffix != null && suffix !== '' ? (
                  <span className="bsf-cidade-picker-item-meta">{suffix}</span>
                ) : null}
                <Check
                  size={14}
                  strokeWidth={2.5}
                  className="bsf-cidade-picker-check"
                  aria-hidden
                />
              </button>
            );
          })
        ) : (
          <div className="bsf-cidade-picker-empty">{emptyMessage}</div>
        )}
      </div>
    </BoardSearchPickerShell>
  );
}
